import React, { useState, useMemo } from 'react';
import { Box, TextInput, Checkbox, Link, Button } from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';
import FindEsicIp from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/components/FindEsicIp';
import { FormFields } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/types';
import { getStatutoryValidationSchema } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/utils';
import {
  FormHeading,
  FormFieldsWrapper,
  StyledForm,
  FormFooterWrapper,
  BorderedBox,
} from 'components/EmployeeOnboarding/components/styles';
import FindPfUan from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/components/FindPfUan';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { useSavedEmployeeData } from './hooks/useSavedEmployeeData';

export const StatutoryInfo: React.FC = () => {
  const {
    employeeDetails: { complianceDetails },
  } = useSavedEmployeeData();
  const [isUanModalOpen, setIsUanModalOpen] = useState(false);
  const [isIpModalOpen, setIsIpModalOpen] = useState(false);
  const history = useHistory();
  const { register, validate, formData } = useFormWithProvider<FormFields>();

  const handleContinue = async () => {
    if (!validate(getStatutoryValidationSchema({
      isPfEnabled: Boolean(complianceDetails.isPfEnabled),
      isEsicEnabled: Boolean(complianceDetails.isEsicEnabled),
    }))) {
      return;
    }
    history.push(routePaths.employeeOnboarding.employeeSelfOnboarding.personalDetails);
  };

  const handleBack = () => {
    history.push(routePaths.employeeOnboarding.employeeSelfOnboarding.bankDetails);
  };

  const isFormValid = useMemo(
    () => validate(getStatutoryValidationSchema({
      isPfEnabled: Boolean(complianceDetails.isPfEnabled),
      isEsicEnabled: Boolean(complianceDetails.isEsicEnabled),
    }), { sideEffects: false }),
    [validate],
  );

  return (
    <>
      <StyledForm>
        <FormFieldsWrapper>
          <FormHeading
            title="Statutory Information"
            description="Fill in the fields below to ensure your compliance and benefits."
          />
          <BorderedBox>
            {Boolean(complianceDetails.isPfEnabled) && (
              <Box>
                <TextInput
                  {...register('pfUan')}
                  label="PF UAN"
                  placeholder="100123456789"
                  size="medium"
                  isDisabled={formData.doNotHavePfUan}
                  necessityIndicator="required"
                  isRequired
                  labelTrailing={
                    <Link size="small" color="primary" onClick={() => setIsUanModalOpen(true)}>
                      How to find your UAN?
                    </Link>
                  }
                />
                <Checkbox
                  isChecked={!!formData.doNotHavePfUan}
                  marginTop="spacing.3"
                  {...register('doNotHavePfUan', 'checkbox')}
                  size="small">
                  I do not have a PF UAN
                </Checkbox>
              </Box>
            )}

            {Boolean(complianceDetails.isEsicEnabled) && (
              <Box>
                <TextInput
                  {...register('esicIpNumber')}
                  label="ESIC IP"
                  placeholder="1234567890"
                  size="medium"
                  isDisabled={formData.doNotHaveEsicIpNumber}
                  necessityIndicator="required"
                  isRequired
                  labelTrailing={
                    <Link size="small" color="primary" onClick={() => setIsIpModalOpen(true)}>
                      How to find your IP Number?
                    </Link>
                  }
                />
                <Checkbox
                  isChecked={!!formData.doNotHaveEsicIpNumber}
                  marginTop="spacing.3"
                  {...register('doNotHaveEsicIpNumber', 'checkbox')}
                  size="small">
                  I do not have an ESIC IP Number
                </Checkbox>
              </Box>
            )}
          </BorderedBox>
        </FormFieldsWrapper>
        <FormFooterWrapper>
          <Button variant="tertiary" size="medium" onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleContinue}
            isDisabled={!isFormValid}>
            Continue
          </Button>
        </FormFooterWrapper>
      </StyledForm>

      <FindPfUan isOpen={isUanModalOpen} onDismiss={() => setIsUanModalOpen(false)} />

      <FindEsicIp isOpen={isIpModalOpen} onDismiss={() => setIsIpModalOpen(false)} />
    </>
  );
};
